import React, { useMemo, useState, useEffect } from 'react';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Direction } from '../../../api/conceptgate';
import { useConceptGate } from '../../../context/ConceptGateContext';
import ConfirmationModal from '../../common/ConfirmationModal';
import { getStoredRiskPercent } from './tradeDefaults';

const TRADE_COLLAPSED_KEY = 'ntb:conceptgate:tradeCollapsed';

// Pip value lookup for common instruments (in USD per pip per standard lot)

const TradeSpecSection = ({ trade, onEdit, onDelete, isExpanded, onToggle }) => {
  const { account } = useConceptGate();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Collapse state - use controlled props if provided (accordion mode), otherwise use internal state
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    const saved = localStorage.getItem(TRADE_COLLAPSED_KEY);
    return saved === 'true';
  });

  // Use controlled state if provided (accordion mode), otherwise use internal state
  const isCollapsed = isExpanded !== undefined ? !isExpanded : internalCollapsed;

  // Persist collapse state only when using internal state
  useEffect(() => {
    if (isExpanded === undefined) {
      localStorage.setItem(TRADE_COLLAPSED_KEY, internalCollapsed.toString());
    }
  }, [internalCollapsed, isExpanded]);

  const toggleCollapse = () => {
    if (onToggle) {
      // Controlled mode (accordion)
      onToggle();
    } else {
      // Internal state mode (backward compatibility)
      setInternalCollapsed(!internalCollapsed);
    }
  };

  // Calculate dynamic values based on current prices
  const calculations = useMemo(() => {
    if (!trade || !trade.entryPrice || !trade.stopPrice) {
      return {
        stopPips: null,
        targetPips: null,
        rr: null,
        riskDollar: null,
        lotSize: null,
      };
    }

    const entry = parseFloat(trade.entryPrice);
    const stop = parseFloat(trade.stopPrice);
    const target = trade.targetPrice ? parseFloat(trade.targetPrice) : null;

    // Helper to detect crypto/stocks
    // Matches backend logic: ends with USDT/BUSD/BTC/ETH or is not 6 chars (simplistic but consistent)
    const isCryptoOrStock = (instrument) => {
      if (!instrument) return false;
      const normalized = instrument.replace('_', '').replace('/', '').toUpperCase();
      if (normalized.endsWith('USDT') || normalized.endsWith('BUSD') || normalized.endsWith('BTC') || normalized.endsWith('ETH')) return true;
      if (normalized.length !== 6) return true;

      const forexCurrencies = new Set(['EUR', 'GBP', 'AUD', 'NZD', 'USD', 'CAD', 'CHF', 'JPY']);
      const base = normalized.substring(0, 3);
      const quote = normalized.substring(3, 6);
      return !(forexCurrencies.has(base) && forexCurrencies.has(quote));
    };

    const isJpyPair = trade.instrument?.includes('JPY');
    const isCrypto = isCryptoOrStock(trade.instrument);

    // Calculate pips
    // Forex: 10000 (standard), 100 (JPY)
    // Crypto/Stock: 100 (1 pip = 0.01)
    let pipMultiplier = 10000;
    if (isJpyPair) pipMultiplier = 100;
    else if (isCrypto) pipMultiplier = 100;

    const stopPips = Math.abs(entry - stop) * pipMultiplier;
    const targetPips = target ? Math.abs(target - entry) * pipMultiplier : null;

    // Calculate R:R
    const rr = targetPips && stopPips > 0 ? targetPips / stopPips : null;

    // Calculate risk in dollars
    const equity = account?.equity || 100000;
    const riskPercent = trade.riskPercent || getStoredRiskPercent();
    const riskDollar = equity * (riskPercent / 100);

    // Calculate lot size
    // Forex: Lot = 100,000 units
    // Crypto/Stock: Lot = 1 unit
    const unitsPerLot = isCrypto ? 1 : 100000;

    // Risk = Units * |Entry - Stop|
    // Units = Lots * UnitsPerLot
    // Lots = Risk / (UnitsPerLot * |Entry - Stop|)

    const priceDist = Math.abs(entry - stop);
    const lotSize = priceDist > 0 ? riskDollar / (priceDist * unitsPerLot) : null;

    return {
      stopPips: Math.round(stopPips * 10) / 10, // Round to 1 decimal
      targetPips: targetPips ? Math.round(targetPips * 10) / 10 : null,
      rr,
      riskDollar,
      riskPercent,
      lotSize,
    };
  }, [trade, account]);

  if (!trade) return null;

  const isLong = trade.direction === Direction.LONG;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    // Use fewer decimals for crypto/indices if needed, but 5 is standard for forex
    return parseFloat(price).toFixed(5);
  };

  const formatPips = (pips) => {
    if (pips === null || pips === undefined) return '-';
    return `${pips} pips`;
  };

  const formatRR = (rr) => {
    if (rr === null || rr === undefined) return '-';
    return `${rr.toFixed(2)}`;
  };

  const formatLots = (lots) => {
    if (lots === null || lots === undefined) return '-';
    return `${lots.toFixed(2)} lots`;
  };

  const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return `$${amount.toFixed(2)}`;
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete();
  };

  return (
    <div className={`conceptgate-section conceptgate-trade-section ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Collapsed View - Always rendered, shown when collapsed */}
      <div className={`trade-collapsed-view ${isCollapsed ? '' : 'hidden'}`}>
        <div className="trade-collapsed-content" onClick={toggleCollapse}>
          <div className="trade-collapsed-header">
            <ShowChartIcon fontSize="small" />
            <span className="conceptgate-instrument">{trade.instrument || 'EURUSD'}</span>
            <span className={`conceptgate-direction-badge compact ${isLong ? 'long' : 'short'}`}>
              {isLong ? <TrendingUpIcon fontSize="inherit" /> : <TrendingDownIcon fontSize="inherit" />}
              {isLong ? 'LONG' : 'SHORT'}
            </span>
            <span className="trade-entry-price">{formatPrice(trade.entryPrice)}</span>
          </div>
          <div className="trade-compact-stats">
            <span className="compact-stat">
              <span className="stat-label">SL</span>
              <span className="stat-value stop">{calculations.stopPips ? `${calculations.stopPips} pips` : '-'}</span>
            </span>
            <span className="compact-stat">
              <span className="stat-label">RR</span>
              <span className="stat-value highlight">{formatRR(calculations.rr)}</span>
            </span>
            <span className="compact-stat">
              <span className="stat-label">Size</span>
              <span className="stat-value">{calculations.lotSize?.toFixed(2) || '-'}</span>
            </span>
            <span className="compact-stat">
              <span className="stat-label">Risk</span>
              <span className="stat-value">{formatMoney(calculations.riskDollar)}</span>
            </span>
          </div>
          <span className="collapse-toggle">
            <ExpandMoreIcon fontSize="small" />
          </span>
        </div>
        <div className="trade-collapsed-actions">
          {onEdit && (
            <button className="trade-compact-btn" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit trade">
              <EditIcon fontSize="small" />
            </button>
          )}
          {onDelete && (
            <button className="trade-compact-btn delete" onClick={(e) => { e.stopPropagation(); handleDeleteClick(); }} title="Delete trade">
              <DeleteOutlineIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded View - Always rendered, shown when expanded */}
      <div className={`expanded-view ${isCollapsed ? 'hidden' : ''}`}>
        <div className="conceptgate-section-header" onClick={toggleCollapse}>
          <ShowChartIcon fontSize="small" />
          <span>Active Trade</span>
          <span className="collapse-toggle" title="Collapse">
            <ExpandLessIcon fontSize="small" />
          </span>

          {onEdit && (
            <button
              className="conceptgate-header-edit-btn"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              title="Edit trade"
            >
              <EditIcon fontSize="small" />
            </button>
          )}
          {onDelete && (
            <button
              className="conceptgate-header-edit-btn delete-btn"
              onClick={(e) => { e.stopPropagation(); handleDeleteClick(); }}
              title="Delete trade"
              style={{ marginLeft: 4 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          )}
        </div>

        <div className="conceptgate-section-content">
            <div className="conceptgate-trade-summary">
              <span className="conceptgate-instrument">{trade.instrument || 'EURUSD'}</span>
              <span className={`conceptgate-direction-badge ${isLong ? 'long' : 'short'}`}>
                {isLong ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                {isLong ? 'LONG' : 'SHORT'}
              </span>
            </div>

            <div className="conceptgate-field-grid">
              <div className="conceptgate-field-row-pair">
                <div className="conceptgate-field-col">
                  <span className="conceptgate-field-label">Entry</span>
                  <span className="conceptgate-field-value">{formatPrice(trade.entryPrice)}</span>
                </div>
                <div className="conceptgate-field-col">
                  <span className="conceptgate-field-label">Stop</span>
                  <span className="conceptgate-field-value stop">
                    {formatPrice(trade.stopPrice)}
                    {calculations.stopPips && <span className="conceptgate-pips">({formatPips(calculations.stopPips)})</span>}
                  </span>
                </div>
              </div>
              <div className="conceptgate-field-row-pair">
                <div className="conceptgate-field-col">
                  <span className="conceptgate-field-label">Target</span>
                  <span className="conceptgate-field-value target">
                    {formatPrice(trade.targetPrice)}
                    {calculations.targetPips && <span className="conceptgate-pips">({formatPips(calculations.targetPips)})</span>}
                  </span>
                </div>
                <div className="conceptgate-field-col">
                  <span className="conceptgate-field-label">R:R</span>
                  <span className="conceptgate-field-value highlight">{formatRR(calculations.rr)}</span>
                </div>
              </div>

              <div className="conceptgate-field-row-pair">
                <div className="conceptgate-field-col">
                  <span className="conceptgate-field-label">Risk ({calculations.riskPercent}%)</span>
                  <span className="conceptgate-field-value">{formatMoney(calculations.riskDollar)}</span>
                </div>
                <div className="conceptgate-field-col">
                  <span className="conceptgate-field-label">Position Size</span>
                  <span className="conceptgate-field-value highlight">{formatLots(calculations.lotSize)}</span>
                </div>
              </div>
            </div>
          </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Trade?"
        message="Are you sure you want to delete this trade setup? This action cannot be undone."
        confirmText="Delete"
        isWarning={true}
      />
    </div>
  );
};

export default TradeSpecSection;
